import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { EdpLinkPreview } from '../api/employeeDocsTypes.js';
import { Alert, Button, Field, Spinner, TextInput } from '../components/ui.js';
import { APP_NAME } from '../lib/branding.js';
import { formatDate } from '../lib/format.js';

/**
 * Wraps the employee and USPS pages: resolves the link, walks the visitor through the
 * one-time code, then renders the page once a package-scoped session exists.
 */
export function LinkAccessShell({
  prefix,
  token,
  heading,
  children,
}: {
  prefix: 'my-documents' | 'usps-documents';
  token: string;
  heading: string;
  children: (preview: EdpLinkPreview, signOut: () => Promise<void>) => ReactNode;
}) {
  const queryClient = useQueryClient();
  const previewKey = ['edp-link', prefix, token];
  const preview = useQuery({
    queryKey: previewKey,
    queryFn: () => apiRequest<EdpLinkPreview>(`/api/${prefix}/${token}`),
    retry: false,
  });

  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`/api/${prefix}/${token}/send-code`, { method: 'POST' });
      setCodeSent(true);
    } catch (cause) {
      setError(cause instanceof ApiRequestError ? cause.message : 'The code could not be sent.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`/api/${prefix}/${token}/verify`, {
        method: 'POST',
        body: { code: code.trim() },
      });
      setCode('');
      await queryClient.invalidateQueries({ queryKey: previewKey });
    } catch (cause) {
      setError(cause instanceof ApiRequestError ? cause.message : 'That code was not accepted.');
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    await apiRequest(`/api/${prefix}/${token}/sign-out`, { method: 'POST' });
    queryClient.removeQueries({ queryKey: ['edp', prefix, token] });
    await queryClient.invalidateQueries({ queryKey: previewKey });
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto w-full max-w-3xl">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{APP_NAME}</p>
          <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">{heading}</h1>
        </div>

        {preview.isLoading ? (
          <Spinner label="Checking your link" />
        ) : preview.isError || !preview.data ? (
          <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <Alert variant="error" title="This link is not valid">
              {preview.error instanceof ApiRequestError
                ? preview.error.message
                : 'It may have expired or been replaced. Contact the person who sent it for a new link.'}
            </Alert>
          </div>
        ) : !preview.data.verified ? (
          <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <h2 className="text-base font-semibold text-slate-900">{preview.data.packageTitle}</h2>
            <p className="mt-1 text-sm text-slate-600">
              For {preview.data.employeeName}. This link works until{' '}
              {formatDate(preview.data.expiresAt)}.
            </p>
            <p className="mt-4 text-sm text-slate-700">
              To protect the documents, we will email a 6-digit code to{' '}
              <span className="font-medium">{preview.data.emailHint}</span>. Enter it below to
              continue.
            </p>
            {error && (
              <Alert variant="error" className="mt-4">
                {error}
              </Alert>
            )}
            <div className="mt-4 flex flex-col gap-4">
              <Button
                variant={codeSent ? 'secondary' : 'primary'}
                loading={busy && !codeSent}
                onClick={() => void sendCode()}
              >
                {codeSent ? 'Send a new code' : 'Email me a code'}
              </Button>
              {codeSent && (
                <form onSubmit={(event) => void verify(event)} className="flex flex-col gap-3">
                  <Alert variant="success">
                    Code sent. It expires in a few minutes and can be used once.
                  </Alert>
                  <Field label="6-digit code" htmlFor="edp-code" required>
                    <TextInput
                      id="edp-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="\d{6}"
                      maxLength={6}
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                      required
                    />
                  </Field>
                  <Button type="submit" loading={busy} disabled={code.length !== 6}>
                    Continue
                  </Button>
                </form>
              )}
            </div>
          </div>
        ) : (
          children(preview.data, signOut)
        )}
      </div>
    </div>
  );
}
